import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle, Upload, PackagePlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ImportDialog from "../components/ImportDialog";

const EMPTY = { sku: "", name: "", category: "Sembako", unit: "pcs", purchase_price: 0, sale_price: 0, min_stock: 10, stock: 0 };
const CATEGORIES = ["Sembako", "Elektronik", "Tekstil", "Material", "Lainnya"];

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [stockDlg, setStockDlg] = useState({ open: false, product: null, qty: 0, reason: "pembelian_tunai", new_purchase_price: 0, note: "" });

  const openStockAdjust = (p) => setStockDlg({ open: true, product: p, qty: 0, reason: "pembelian_tunai", new_purchase_price: p.purchase_price, note: "" });
  const submitStockAdjust = async () => {
    const qty = Number(stockDlg.qty);
    if (!qty) return toast.error("Qty tidak boleh 0");
    // Reason tertentu wajib qty negatif
    const negReason = ["opname_minus", "retur_supplier"];
    const posReason = ["pembelian_tunai", "pembelian_transfer", "opname_plus"];
    let finalQty = qty;
    if (negReason.includes(stockDlg.reason) && qty > 0) finalQty = -qty;
    if (posReason.includes(stockDlg.reason) && qty < 0) finalQty = Math.abs(qty);
    try {
      await api.post(`/products/${stockDlg.product.id}/stock-adjust`, {
        qty: finalQty,
        reason: stockDlg.reason,
        new_purchase_price: Number(stockDlg.new_purchase_price) || null,
        note: stockDlg.note,
      });
      toast.success(`Stok ${stockDlg.product.sku} berhasil ${finalQty > 0 ? "ditambah" : "dikurangi"} ${Math.abs(finalQty)}`);
      setStockDlg({ ...stockDlg, open: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal adjust stok"); }
  };

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditingId(p.id); setOpen(true); };

  const save = async () => {
    try {
      if (!form.sku || !form.name) return toast.error("SKU & Nama wajib diisi");
      const payload = {
        ...form,
        purchase_price: Number(form.purchase_price),
        sale_price: Number(form.sale_price),
        min_stock: Number(form.min_stock),
        stock: Number(form.stock),
      };
      if (editingId) await api.put(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      toast.success("Produk tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}" (SKU: ${p.sku})?\nAksi ini tidak bisa dibatalkan. Produk dengan riwayat transaksi sebaiknya dinonaktifkan, bukan dihapus.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const filtered = items.filter((p) =>
    (cat === "all" || p.category === cat) &&
    (p.name.toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase()))
  );

  const totalValue = filtered.reduce((s, p) => s + (p.stock * p.purchase_price), 0);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Gudang</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola SKU, kategori, dan level stok minimum.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="product-import-btn"><Upload size={14} className="mr-1" />Import Excel</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="product-add-btn">
              <Plus size={16} className="mr-2" /> Tambah Produk
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Produk" : "Produk Baru"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>SKU</Label><Input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} disabled={!!editingId} /></div>
              <div><Label>Satuan</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
              <div className="col-span-2"><Label>Nama Barang</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Harga Beli (Rp)</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></div>
              <div><Label>Harga Jual (Rp)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></div>
              <div><Label>Stok</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} disabled={!!editingId} /></div>
              <div><Label>Min. Stok</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="product-save-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total SKU</div><div className="font-display text-2xl font-extrabold mt-1">{items.length}</div></CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Nilai Stok (Modal)</div><div className="font-display text-2xl font-extrabold mt-1">{formatIDR(totalValue)}</div></CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Stok Menipis</div><div className="font-display text-2xl font-extrabold mt-1 text-red-600">{items.filter(p => p.stock < p.min_stock).length}</div></CardContent></Card>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Input data-testid="product-search" placeholder="Cari SKU atau nama..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="product-category-filter" className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="products-table">
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead className="text-right">Harga Beli</TableHead>
                  <TableHead className="text-right">Harga Jual</TableHead>
                  <TableHead className="text-right">Stok</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const low = p.stock < p.min_stock;
                  return (
                    <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                      <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(p.purchase_price)}</TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(p.sale_price)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{p.stock} {p.unit}</TableCell>
                      <TableCell>
                        {low ? (
                          <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100"><AlertTriangle size={12} className="mr-1" /> Menipis</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Aman</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => openStockAdjust(p)} data-testid={`product-stock-adjust-${p.sku}`} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"><PackagePlus size={14} className="mr-1" />Stok</Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`product-edit-${p.sku}`} className="ml-1"><Pencil size={14} /></Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`product-delete-${p.sku}`} className="ml-1"><Trash2 size={14} className="text-red-500" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Tidak ada data</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <ImportDialog
        open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load}
        title="Import Inventory dari Excel/CSV"
        endpoint="/products/import"
        templateName="template_inventory"
        columns={[
          { key: "sku", label: "SKU", required: true, type: "string" },
          { key: "name", label: "Nama Barang", required: true, type: "string" },
          { key: "category", label: "Kategori", type: "string" },
          { key: "unit", label: "Satuan", type: "string" },
          { key: "purchase_price", label: "Harga Beli", type: "number" },
          { key: "sale_price", label: "Harga Jual", type: "number" },
          { key: "stock", label: "Stok Awal", type: "number" },
          { key: "min_stock", label: "Min. Stok", type: "number" },
        ]}
        sampleRow={{ sku: "SKU-001", name: "Contoh Barang", category: "Sembako", unit: "pcs", purchase_price: 10000, sale_price: 15000, stock: 100, min_stock: 10 }}
      />

      {/* Stock Adjustment Dialog */}
      <Dialog open={stockDlg.open} onOpenChange={(v) => !v && setStockDlg({ ...stockDlg, open: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><PackagePlus size={18} />Tambah / Sesuaikan Stok</DialogTitle></DialogHeader>
          {stockDlg.product && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded p-3">
                <div className="text-[11px] uppercase text-slate-500">Produk</div>
                <div className="font-bold text-navy-900">{stockDlg.product.sku} — {stockDlg.product.name}</div>
                <div className="text-xs text-slate-500">Stok sekarang: <strong className="text-navy-900 font-mono">{stockDlg.product.stock} {stockDlg.product.unit}</strong></div>
              </div>
              <div>
                <Label>Alasan Penyesuaian *</Label>
                <Select value={stockDlg.reason} onValueChange={(v) => setStockDlg({ ...stockDlg, reason: v })}>
                  <SelectTrigger data-testid="stock-adj-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pembelian_tunai">Pembelian Tunai (Cash → Persediaan)</SelectItem>
                    <SelectItem value="pembelian_transfer">Pembelian Transfer Bank (Bank → Persediaan)</SelectItem>
                    <SelectItem value="opname_plus">Opname Kelebihan (+ stok)</SelectItem>
                    <SelectItem value="opname_minus">Opname Kekurangan (− stok)</SelectItem>
                    <SelectItem value="retur_supplier">Retur ke Supplier (− stok, − Utang)</SelectItem>
                    <SelectItem value="adjustment">Penyesuaian Umum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Qty *</Label>
                  <Input type="number" value={stockDlg.qty} onChange={(e) => setStockDlg({ ...stockDlg, qty: e.target.value })} data-testid="stock-adj-qty" />
                  <div className="text-[10px] text-slate-500 mt-1">Untuk retur/opname minus, cukup angka positif — sistem otomatis kurangi</div>
                </div>
                <div>
                  <Label>Harga Beli Baru <span className="text-slate-400 text-xs">(opsional)</span></Label>
                  <Input type="number" value={stockDlg.new_purchase_price} onChange={(e) => setStockDlg({ ...stockDlg, new_purchase_price: e.target.value })} data-testid="stock-adj-price" />
                  <div className="text-[10px] text-slate-500 mt-1">Ubah bila harga supplier baru berbeda</div>
                </div>
              </div>
              <div>
                <Label>Catatan</Label>
                <Input value={stockDlg.note} onChange={(e) => setStockDlg({ ...stockDlg, note: e.target.value })} placeholder="Cth: nota supplier X, opname 30 Sept" data-testid="stock-adj-note" />
              </div>
              <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 text-yellow-900">
                Jurnal otomatis dipost sesuai alasan (Dr/Cr Kas, Bank, Persediaan, Utang, atau Beban Selisih).
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDlg({ ...stockDlg, open: false })}>Batal</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-600 text-white" onClick={submitStockAdjust} data-testid="stock-adj-submit"><PackagePlus size={14} className="mr-1" />Simpan & Post Jurnal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
